import { Dropdown, Select } from 'antd';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Ajax } from '../../components/Ajax';
import { Input } from '../../components/Translations';
import { ApiUrl } from '../../project/Defines.js';
import { useCustomEvent } from '../../project/utilities';
import ListView from '../../Shared/ListView';
import { useSWCache } from '../../Shared/SWCache';

const { Option } = Select;

/**
 * specific case of DropDownValueList with 'multiple' mode
 * @param {any} props valuelist, items, parentValue, additionalData, reloadEventName, ...restprops
 */
export function MultiSelectValueList(props) {
    return <DropDownValueList {...props} mode='multiple' />
}

export function DoLoadValueList(ref, valuelist, parentValue, additionalData, disableCach, setCacheData, setDataSource, manageValue, setLoaded) {// NOSONAR
    if (valuelist) {
        if (typeof valuelist === 'string') {
            Ajax.post({
                url: ApiUrl.ValueList,
                data: {
                    valueList: valuelist,
                    parentValue: parentValue || "",
                    additionalData: additionalData || ""
                },
                success: function (response) {
                    if (response && ref.current) {
                        const data = response;
                        for (const element of data) {
                            element.key = element.uuid;
                        }
                        setDataSource(data);
                        setLoaded(true);
                        manageValue && manageValue(data);
                        !disableCach && setCacheData(data);
                    }
                }
            });
        } else {
            setDataSource(valuelist);
            setLoaded(true);
            manageValue && manageValue(valuelist);
        }
    }
}


/**
 * custom DropDownValueList working with backend value lists and custom given items
 * @param {object} props   valuelist, mode, items, parentValue, additionalData, reloadEventName, ...restprops
 */
export function DropDownValueList(props) {
    const [loaded, setLoaded] = useState(false);
    const [dataSource, setDataSource] = useState([]);
    const { value, restrictCustomValue, allowClear, valuelist, mode, size, items, parentValue, additionalData, reloadEventName, disableCach, culture, autoSelectFirst, dispatch, searchOptions, textAsValue, disableScroll, ...restprops } = props;
    const ref = useRef(false);

    const { getCacheData, setCacheData } = useSWCache({ key: `${culture}_${valuelist}`, isClearOnLogout: true });
    const manageValue = useCallback((data) => {
        if ((restrictCustomValue && !mode) && value && !data.some(item => item[textAsValue ? 'text' : 'value'] === value)) {
            props.onChange && props.onChange(autoSelectFirst ? data[0].value : '');
        } else if (autoSelectFirst && !value) {
            props.onChange && props.onChange(data[0].value);
        }

    }, [value, autoSelectFirst, textAsValue, restrictCustomValue, mode, props])

    const loadData = useCallback(() => {// NOSONAR
        DoLoadValueList(ref, valuelist, parentValue, additionalData, disableCach, setCacheData, setDataSource, manageValue, setLoaded);
    }, [valuelist, parentValue, additionalData, autoSelectFirst, disableCach, setCacheData, manageValue]);// eslint-disable-line react-hooks/exhaustive-deps

    useCustomEvent(reloadEventName || 'reload_valuelist_' + valuelist, loadData);

    useEffect(() => {
        ref.current = true;
        if (items) {
            setDataSource([...items]);
            setLoaded(true);
            manageValue(items);
        }
        else {
            if (disableCach) {
                loadData();
            } else {
                getCacheData().then((data) => {
                    if (ref.current) {
                        if (data) {
                            setDataSource(data);
                            setLoaded(true);
                            manageValue(data);
                        } else {
                            loadData();
                        }
                    }
                });
            }
        }
        return () => { ref.current = false }
    }, [additionalData, valuelist, parentValue]);// eslint-disable-line react-hooks/exhaustive-deps

    const filterOption = (input, option) => {
        let text = searchOptions.caseSensitive ? option.text : option.text && option.text.toLowerCase();
        let searchInput = searchOptions.caseSensitive ? input : input && input.toLowerCase();
        if (searchOptions.noAccent) {
            text = text.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
            searchInput = searchInput.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
        }
        return !searchInput || (text && (searchOptions.contains ? text.includes(searchInput) : text.startsWith(searchInput)));
    };


    return <>
        {loaded && <Select
            allowClear={allowClear}
            size={size || "default"}
            mode={mode}
            virtual={true}
            value={value}
            {...restprops}
            onChange={(e) => props.onChange(e, dataSource)} // NOSONAR
            filterOption={searchOptions && filterOption}
            showSearch={!!searchOptions}
            {...(!disableScroll ? { getPopupContainer: (trigger) => trigger.parentElement } : null)}
        >
            {
                dataSource.map((item, i) =>
                    <Option
                        key={i} // NOSONAR
                        value={textAsValue ? item.text : item.value} text={item.text}>
                        {item.text}
                    </Option>
                )
            }
        </Select>}
    </>
}

/**
 * custom MultiColumnDropDown working with backend value lists and custom given items
 * @param {object} props   name, valuelist, searchOptions, parentValue, label, required, disabled, columns, listLoadEvent, fieldsToShow, additionalData, disableScroll, value, ...restprops
 */
export const MultiColumnDropDown = (props) => {
    const { name, valuelist, searchOptions, parentValue, label, required, disabled, columns, listLoadEvent, fieldsToShow, additionalData, disableScroll, value, onChange, renderText, valueAsText, showHeader, textField, valueField, noPaging, skipNewButton, mode, maxSelection, ...restprops } = props;
    const [inputVal, setInputVal] = useState("");
    const [data, setData] = useState([]);
    const allDataRef = useRef([]);
    const [focusStyle, setFocusStyle] = useState(false);
    const [visible, setVisible] = useState(false);
    const [placeholder, setPlaceholder] = useState("");
    const ref = useRef(false);
    const inputRef = useRef();
    const isRowClickRef = useRef(false);

    useEffect(() => {
        loadData();
    }, [valuelist, parentValue]);// eslint-disable-line react-hooks/exhaustive-deps

    const isSameValue = useCallback((a, b) => {
        if (a === null || a === undefined || b === null || b === undefined) return false;
        return String(a) === String(b);
    }, []);

    const getRecordValue = useCallback((record) => {
        if (typeof valueField === 'function') return valueField(record);
        if (valueField) return record[valueField];
        return record.ID;
    }, [valueField]);

    const getRecordText = useCallback((record) => {
        if (renderText) return renderText(record);
        if (typeof textField === 'function') return textField(record);
        if (textField) return record[textField];
        return '';
    }, [renderText, textField]);

    const buildDisplayText = useCallback((selectedValue, sourceData) => {
        if (mode !== 'multiple') return '';
        if (!Array.isArray(selectedValue) || selectedValue.length === 0) return '';
        return selectedValue.map((sel) => {
            const matched = sourceData.find((r) => isSameValue(getRecordValue(r), sel));
            return matched ? getRecordText(matched) : sel;
        }).join(', ');
    }, [mode, isSameValue, getRecordValue, getRecordText]);

    useEffect(() => {
        if (mode === 'multiple') {
            // tags handle display — nothing to do with inputVal
        }
    }, [value, data]);// eslint-disable-line react-hooks/exhaustive-deps

    const filterOption = useCallback((input, data) => {// NOSONAR
        let searchInput = searchOptions.caseSensitive ? input : input && input.toLowerCase();
        let includes = false;
        let filteredData = [];
        let text = "";

        if (searchOptions.noAccent) {
            searchInput = searchInput.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
        }

        filteredData = data.filter((d) => {
            for (let [key, value] of Object.entries(d)) {
                if (key !== "ID") {
                    text = value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
                    includes = searchOptions.contains ? text.includes(searchInput) : text.startsWith(searchInput);

                    if (includes) {
                        break;
                    }
                }
            }

            return includes;
        });

        return filteredData;
    }, [searchOptions]);

    const loadData = useCallback((input) => {
        Ajax.post({
            url: ApiUrl.ValueList,
            data: {
                valueList: valuelist,
                parentValue: parentValue || "",
                additionalData: additionalData || ""
            },
            success: function (response) {
                if (response.length > 0) {
                    let _data = input ? filterOption(input, response) : response;

                    _data = _data.map(item => {
                        item.key = item.ID;
                        return item;
                    });

                    if (!input) {
                        allDataRef.current = _data; // keep full list for tag lookups
                    }
                    setData(_data);
                } else {
                    if (!input) allDataRef.current = [];
                    setData([]);
                }
            }
        });
    }, [additionalData, valuelist, parentValue, filterOption]);

    const onSelect = useCallback((record) => {
        const selectedValue = getRecordValue(record);

        if (mode === 'multiple') {
            const currentValues = Array.isArray(value) ? value : [];
            const exists = currentValues.some((item) => isSameValue(item, selectedValue));
            let nextValues = exists
                ? currentValues.filter((item) => !isSameValue(item, selectedValue))
                : [...currentValues, selectedValue];

            if (!exists && maxSelection && nextValues.length > maxSelection) {
                nextValues = currentValues;
            }

            onChange && onChange(nextValues);
            isRowClickRef.current = true;
            setInputVal("");
            loadData(); // reset filtered list after selection
            return; // keep dropdown open
        }

        const text = getRecordText(record);
        setInputVal(text);
        setPlaceholder(text);
        onChange && onChange(record);

        setVisible(false);
    }, [mode, value, onChange, getRecordValue, getRecordText, isSameValue, maxSelection]);

    const onOpen = () => {
        setVisible(true);
        setInputVal("");
        loadData(); // reset to full list on open
        setTimeout(() => { inputRef.current && inputRef.current.focus(); }, 50);
    };

    const handleVisibleChange = (isVisible) => {
        // In multi mode, suppress close only when it was triggered by a row click
        if (!isVisible && mode === 'multiple' && isRowClickRef.current) {
            isRowClickRef.current = false;
            setVisible(isVisible);
            return;
        }
        isRowClickRef.current = false;
        setVisible(isVisible);
        if (isVisible) {
            inputRef.current.focus();
            setInputVal("");
        } else {
            if (mode !== 'multiple') setInputVal(placeholder);
            else {
                setInputVal("");
                loadData(); // reset filtered list on close
            }
        }
    };

    const overlay = (
        <view className="ant-dropdown-menu multi-column-dropdown" >
            <ListView
                dataSource={data}
                columnConfig={columns}
                noPaging={noPaging}
                apiUrl={ApiUrl.ValueList}
                listUpdateEvent={listLoadEvent}
                data={{ ...additionalData }}
                onClick={(record) => { // NOSONAR
                    onSelect(record);
                }}
                skipNewButton={skipNewButton}
                showHeader={showHeader}
                skipEdit
                onDoubleClick={(record) => { onSelect(record); }} // NOSONAR
                className={"list"}
                rowClassName={(record) => { // NOSONAR
                    const recVal = getRecordValue(record);
                    if (mode === 'multiple') {
                        return Array.isArray(value) && value.some((item) => isSameValue(item, recVal)) ? 'select' : '';
                    }
                    return isSameValue(value, recVal) ? 'select' : '';
                }}
            />
        </view>
    );

    return <Dropdown
        disabled={disabled}
        className={focusStyle && "ant-select-focused "}
        trigger={['click']}
        overlay={overlay}
        overlayStyle={{ width: 500, maxHeight: 300, overflow: "auto" }}
        open={visible && !disabled}
        onFocus={() => setFocusStyle(true)} // NOSONAR
        onBlur={() => setFocusStyle(false)} // NOSONAR
        onOpenChange={handleVisibleChange} // NOSONAR
        {...(!disableScroll ? { getPopupContainer: (trigger) => trigger.parentElement } : null)}
        {...restprops}
    >
        <div label={label} className={"ant-select ant-select-selector" + (mode === 'multiple' ? " ant-select-multiple" : " ")} onClick={onOpen}
            style={mode === 'multiple' ? { position: 'relative', minHeight: 32, height: 'auto', display: 'flex', flexWrap: 'wrap', alignItems: 'center', margin: 10, cursor: 'pointer' } : {}}>
            {mode === 'multiple' ? (
                <>
                    {Array.isArray(value) && value.map((val) => {
                        const matched = allDataRef.current.find((r) => isSameValue(getRecordValue(r), val));
                        const text = matched ? getRecordText(matched) : String(val);
                        return (
                            <span key={val} className="ant-select-selection-item" style={{ margin: 5 }}>
                                <span className="ant-select-selection-item-content" style={{ height: 20 }}>{text}</span>
                                <span onClick={(e) => { // NOSONAR
                                    e.stopPropagation();
                                    const nextValues = (Array.isArray(value) ? value : []).filter(v => !isSameValue(v, val));
                                    onChange && onChange(nextValues);
                                }}
                                    style={{ display: 'inline-flex', alignItems: 'center', verticalAlign: 'middle' }}><icon small="" style={{ fontSize: 25 }}>close</icon></span>
                            </span>
                        );
                    })}
                    <span className="ant-select-selection-search" style={{ flex: 1, minWidth: 60 }}>
                        <Input autoComplete="off"
                            ref={inputRef}
                            style={{ width: "100%", border: 'none', outline: 'none', boxShadow: 'none' }}
                            disabled={disabled}
                            value={inputVal}
                            onChange={(e) => { // NOSONAR
                                loadData(e.target.value);
                                setInputVal(e.target.value);
                            }}
                            name={name}
                            required={required}
                            placeholder={(!Array.isArray(value) || value.length === 0) ? placeholder : ''}
                        />
                    </span>
                </>
            ) : (
                <span className="ant-select-selection-item">
                    <Input autoComplete="off"
                        ref={inputRef}
                        style={{ width: "100%" }}
                        disabled={disabled}
                        value={inputVal}
                        onChange={(e) => { // NOSONAR
                            loadData(e.target.value);
                            setInputVal(e.target.value);
                        }}
                        name={name}
                        required={required}
                        placeholder={placeholder}
                    />
                </span>
            )}
            <span className="ant-select-arrow" style={mode === 'multiple' ? { position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)' } : {}}>
                <span role="img" aria-label="down" className={"anticon anticon-" + (visible ? "search" : "down") + " ant-select-suffix"}>
                </span>
            </span>
        </div>
    </Dropdown>
}

MultiColumnDropDown.propTypes = {
    additionalData: PropTypes.object,
    serverSideFiltering: PropTypes.bool,
    hasSearch: PropTypes.bool

};
MultiColumnDropDown.defaultProps = {
    additionalData: {},
    serverSideFiltering: true,
    hasSearch: true
};

export default connect(state => ({ culture: state.culture }))(DropDownValueList);